"use client"

import { usePathname, useSearchParams } from "next/navigation"
import {
  createRef,
  ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react"
import smoothscroll from "smoothscroll-polyfill"
import { Provider, RefsRegister } from "./context"
import { debounce } from "./utils"

type Props = {
  debounceDelay?: number
  scrollBehavior?: "auto" | "smooth"
  offset?: number
  children: ReactNode
}

// Apply smoothscroll polyfill once in the browser
if (typeof window !== "undefined") {
  smoothscroll.polyfill()
}

export default function ScrollingProvider({
  debounceDelay = 20,
  scrollBehavior = "smooth",
  offset = 0,
  children,
}: Props) {
  const [selected, setSelected] = useState("")
  const REFS: RefsRegister = useMemo(() => ({}), [])
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // 🧹 Clean up stale refs on URL change
  useEffect(() => {
    Object.keys(REFS).forEach((key) => {
      if (REFS[key]?.current == null) {
        delete REFS[key]
      }
    })
  }, [pathname, searchParams])

  // 🧭 Handle initial scroll to hash
  useEffect(() => {
    document.addEventListener("scroll", debounceScroll, true)

    const id = decodeURIComponent(window.location.hash.replace(/^#/, ""))
    const sectionRef = REFS[id]

    if (sectionRef?.current) {
      const top = sectionRef.current.offsetTop + offset
      window.scrollTo({ top, behavior: "auto" })
      if (selected !== id) {
        window.history.replaceState(
          history.state,
          "",
          `${window?.location?.pathname}${window?.location?.search}#${id}`
        )
        setSelected(id)
      }
    } else {
      handleScroll()
    }

    return () => {
      document.removeEventListener("scroll", debounceScroll, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScroll = () => {
    const selectedSection = Object.keys(REFS).reduce(
      (acc, id) => {
        const el = REFS[id].current
        if (!el) return acc
        const { top, bottom } = el.getBoundingClientRect()
        if (top > offset + 100 || bottom < offset + 100) return acc
        if (top <= acc.top) return { top, id }
        return acc
      },
      { top: 9999, id: "" }
    )

    if (selected !== selectedSection.id) {
      const newHash = `#${selectedSection.id}`
      if (window.location.hash !== newHash) {
        window.history.replaceState(
          history.state,
          "",
          `${window?.location?.pathname}${window?.location?.search}${newHash}`
        )
      }
      setSelected(selectedSection.id)
    }
  }

  const debounceScroll = debounce(handleScroll, debounceDelay)

  const registerRef = (id: string) => {
    const ref = createRef<HTMLElement | null>()
    REFS[id] = ref
    return ref
  }

  const scrollTo = (section: string) => {
    const sectionRef = REFS[section]
    if (!sectionRef?.current) {
      console.warn(`Section ID "${section}" not recognized!`)
      return
    }

    const top =
      sectionRef.current.getBoundingClientRect().top -
      document.body.getBoundingClientRect().top -
      offset

    const newHash = section ? `#${section}` : ""
    if (window.location.hash !== newHash) {
      window.history.replaceState(
        history.state,
        "",
        `${window?.location?.pathname}${window?.location?.search}${newHash}`
      )
    }

    setSelected(section)
    window.scrollTo({ top, behavior: scrollBehavior })
  }

  const value = useMemo(
    () => ({ registerRef, scrollTo, refs: REFS, selected }),
    [selected, REFS]
  )

  return <Provider value={value}>{children}</Provider>
}

// Wrapper with Suspense boundary
export function ScrollingProviderWithSuspense(props: Props) {
  return (
    <Suspense fallback={<div>{props.children}</div>}>
      <ScrollingProvider {...props} />
    </Suspense>
  )
}
